import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import cn from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputControlled, SelectControlled, Loading } from 'components';
import { NewPatientStep } from './NewPatientStep';
import { useParams } from 'react-router-dom';
import { PatientContext } from 'contexts/PatientContext';
import { showError } from 'utils/alerts';
import {
	dxSubtypes_OPTIONS,
	fitOrUnfit_OPTIONS,
	dxSecondary_OPTIONS,
	yesOrNo_OPTIONS,
	riskLevel_OPTIONS,
  INTITUTION_TYPES,
} from 'utils/constants';
import {
	getPatientDiagnostic,
	newPatientDiagnostic,
	editPatientDiagnostic,
} from 'services/patients';
import { useAuth } from 'contexts/AuthContext';

interface DiagnosticPageProps {}

interface IFormInputs {
	dxSubType: string;
	subTypeName?: string;
	dxSecondary: string;
	patientFit: string;
	// organsFailures: boolean;
	dxDate: Date;
	pacientFit: string;
	// riskLevel: string;
	relapse: boolean;
	relapseDate: Date;
	consent: boolean;
	consentDate: Date;
	dxSecondaryDescription: string;
}

export const Diagnostic: React.FunctionComponent<DiagnosticPageProps> = () => {
	const { t } = useTranslation();
	const { user } = useAuth();
  const isBioBancoInstitution =
  user.doctor.institution.type === INTITUTION_TYPES["BIOBANK"];
	const [key, setKey] = useState(0);
	const { isAdmin } = user;
	const { patient, setPatient, fetching, fetchPatient } =
		useContext<any>(PatientContext);
	const { patientId } = useParams();

	const schema = yup
		.object({
			dxSubType: yup
				.string()
				.nullable() /* .required(t('error.mustChooseAnOption')) */,
			subTypeName: yup.string().when('dxSubType', {
				is: 'Other',
				then: yup.string().required(t('error.requiredField')),
			}),
			dxSecondary: yup
				.string()
				.nullable() /* .required(t('error.mustChooseAnOption')) */,
			patientFit: yup
				.string()
				.nullable() /* .required(t('error.mustChooseAnOption')) */,
			dxDate: yup
				.date()
				.nullable()
				.default(undefined)
				.transform((curr, orig) => (orig === '' ? null : curr))
				.typeError(t('error.mustChooseADate')),
			relapse: yup
				.boolean()
				.nullable() /* .required(t('error.mustChooseAnOption'))*/,
			relapseDate: yup
				.date()
				.nullable()
				.when('relapse', {
					is: true,
					then: yup
						.date()
						.typeError(t('error.mustChooseADate'))
						.required(t('error.mustChooseADate')),
				}),
			consent: yup
				.boolean()
				.default(false)
				.required(t('error.mustChooseAnOption')),
			consentDate: yup
				.date()
				.nullable()
				.when('consent', {
					is: true,
					then: yup
						.date()
						.typeError(t('error.mustChooseADate'))
						.required(t('error.mustChooseADate')),
				}),
			dxSecondaryDescription: yup.string().nullable()
		})
		.required();

	const {
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors, dirtyFields, isValid },
	} = useForm<IFormInputs>({
		resolver: yupResolver(schema),
		mode: 'onChange',
	});

	const isRelapse = watch('relapse');
	const isConsent = watch('consent');
	const isOther = watch('dxSubType');
	const isSecondary = watch('dxSecondary')

	useEffect(() => {
		if (patient?.diagnostics?.length > 0 && patient.diagnostics[0].dxSubType)
			setValue('dxSubType', patient.diagnostics[0].dxSubType);
	}, [patient]);

	const handleOnSubmit: SubmitHandler<IFormInputs> = async (data: any) => {
		try {
			const modifiedFields = Object.keys(dirtyFields);

			if (modifiedFields.length > 0) {
				if (patientId && patient?.diagnostics?.length > 0) {
					let updateData: IFormInputs | {} = {};
					modifiedFields.map((fieldName: string) => {
						if (fieldName === 'relapseDate' && data[fieldName]) {
							updateData = {
								...updateData,
								relapse: true,
								[fieldName]: data[fieldName],
							};
						} else if (fieldName === 'consentDate' && data[fieldName]) {
							updateData = {
								...updateData,
								consent: true,
								[fieldName]: data[fieldName],
							};
						} else if (
							fieldName === 'dxSubType' &&
							data.dxSubType.includes('Other') &&
							data.subTypeName
						) {
							updateData = {
								...updateData,
								//@ts-ignore
								dxSubType: `Other: ${data.subTypeName}`,
							};
						} else if (fieldName !== 'subTypeName') {
							updateData = { ...updateData, [fieldName]: data[fieldName] };
						}
					});
					const diagnosticId = patient.diagnostics[0].id;
					await editPatientDiagnostic(diagnosticId, updateData);
				} else {
					const diagnosticData = { ...data };
					if (data.dxSubType.includes('Other') && data.subTypeName) {
						diagnosticData.dxSubType = `Other: ${data.subTypeName}`;
					}
					delete diagnosticData.subTypeName;
					await newPatientDiagnostic({
						patientId: patient?.id,
						...diagnosticData,
					});
				}
				await fetchPatient();
			}
		} catch (error) {
			console.log({ error });
			showError(t('error.savePatientInfo'));
		}
	};

	return (
		<NewPatientStep
			title={`03.- ${t('diagnostic')}`}
			prevStep='/samples'
			nextStep='/treatments'
			handleOnClickNext={handleSubmit(handleOnSubmit)}
			isValid={isValid}
		>
			{fetching ? (
				<Loading />
			) : (
				<form
					key={key}
					onSubmit={handleSubmit(handleOnSubmit)}
					className='grid lg:grid-cols-4 grid-cols-1 gap-6'
				>
					<SelectControlled
						name='dxSubType'
						disabled={isAdmin}
						className='lg:col-span-2'
						control={control}
						labelClass='sm'
						options={dxSubtypes_OPTIONS}
						defaultValue={
							patient?.diagnostics?.length > 0
								? patient?.diagnostics[0]?.dxSubType?.includes('Other')
									? {
											value: 'Other',
											label: t('other'),
									  }
									: {
											value: patient.diagnostics[0].dxSubType,
											label: dxSubtypes_OPTIONS.find((dxSubType) => dxSubType.value === patient.diagnostics[0].dxSubType)!.label,
									  }
								: ''
						}
						label={t('dxSubType')}
						//@ts-ignore
						errors={errors.dxSubType}
					/>
					<SelectControlled
						name='dxSecondary'
						disabled={isAdmin}
						control={control}
						defaultValue={
							patient?.diagnostics?.length > 0
								? {
										value: patient.diagnostics[0].dxSecondary,
										label: patient.diagnostics[0].dxSecondary,
								  }
								: ''
						}
						className='lg:col-span-2'
						labelClass='sm whitespace-nowrap'
						options={dxSecondary_OPTIONS}
						label={t('dxSecondary_full')}
						//@ts-ignore
						errors={errors.dxSecondary}
					/>
					{isSecondary?.includes('Secundario') &&	<InputControlled
							disabled={isAdmin}
							name='dxSecondaryDescription'
							control={control}
							defaultValue={
								patient?.diagnostics?.length > 0
									? patient?.diagnostics[0]?.dxSubType
										? patient.diagnostics[0].dxSubType?.slice(6)
										: ''
									: ''
							}
							className='lg:col-span-4'
							labelClass='sm'
							label={t('dxSecondaryDescription')}
							//@ts-ignore
							errors={errors.dxSubTypeName}
						/>}
					{isOther?.includes('Other') && (
						<InputControlled
							name='subTypeName'
							disabled={isAdmin}
							type='textarea'
							control={control}
							defaultValue={
								patient?.diagnostics?.length > 0
									? patient?.diagnostics[0]?.dxSubType
										? patient.diagnostics[0].dxSubType?.slice(6)
										: ''
									: ''
							}
							className='lg:col-span-4'
							labelClass='sm'
							label={t('detail')}
							//@ts-ignore
							errors={errors.dxSubTypeName}
						/>
					)}
					{!isOther?.includes('Other') && (
						<>
							<SelectControlled
								name='patientFit'
								disabled={isAdmin}
								control={control}
								defaultValue={
									patient?.diagnostics?.length > 0
										? {
												value: patient.diagnostics[0].patientFit,
												label: patient.diagnostics[0].patientFit,
										  }
										: ''
								}
								className='lg:col-span-1'
								labelClass='sm'
								options={fitOrUnfit_OPTIONS}
								label={t('pacientFit')}
								//@ts-ignore
								errors={errors.patientFit}
							/>
							<InputControlled
								name='dxDate'
								disabled={isAdmin}
								control={control}
								defaultValue={
									patient?.diagnostics?.length > 0
										? patient.diagnostics[0].dxDate
										: ''
								}
								className='lg:col-span-1'
								inputClasses='max-h-[50px]'
								labelClass='sm'
								type='date'
								label={t('dxDate')}
								//@ts-ignore
								errors={errors.dxDate}
							/>
							<SelectControlled
								name='relapse'
								disabled={isAdmin}
								control={control}
								defaultValue={
									patient?.diagnostics?.length > 0
										? {
												value: patient.diagnostics[0].relapse,
												label: patient.diagnostics[0].relapse
													? t('yes')
													: t('no'),
										  }
										: { value: false, label: 'No' }
								}
								className='lg:col-span-1'
								labelClass='sm'
								options={yesOrNo_OPTIONS}
								label={t('relapse')}
								//@ts-ignore
								errors={errors.relapse}
							/>
							{(!!isRelapse ||
								(patient?.diagnostics && patient.diagnostics[0]?.relapse)) && (
								<InputControlled
									name='relapseDate'
									disabled={isAdmin}
									control={control}
									defaultValue={
										patient?.diagnostics?.length > 0
											? patient.diagnostics[0].relapseDate
											: ''
									}
									className='lg:col-span-1'
									inputClasses='max-h-[50px]'
									labelClass='sm'
									type='date'
									label={t('relapseDate')}
									//@ts-ignore
									errors={errors.relapseDate}
								/>
							)}
						</>
					)}
					<div className='col-span-4 grid lg:grid-cols-4 grid-cols-1 gap-6'>
              <SelectControlled
                  name='consent'
                  disabled={isAdmin}
                  control={control}
                  defaultValue={
                    patient?.diagnostics?.length > 0
                      ? {
                          value: patient.diagnostics[0].consent,
                          label: patient.diagnostics[0].consent
                            ? t('yes')
                            : t('no'),
                        }
                      : {
                          value: false,
                          label: isBioBancoInstitution ? 'Bio Banco' : 'No',
                        }
                  }
                  className='lg:col-span-2'
                  labelClass='sm'
                  options={
                    isBioBancoInstitution
                      ? [{ value: false, label: 'Bio Banco' }]
                      : yesOrNo_OPTIONS
                  }
                  label={t('consent')}
                  //@ts-ignore
                  errors={errors.consent}
                />

						{(!!isConsent ||
							(patient?.diagnostics && patient.diagnostics[0]?.consent)) && (
							<InputControlled
								name='consentDate'
								disabled={isAdmin}
								control={control}
								defaultValue={
									patient?.diagnostics?.length > 0
										? patient.diagnostics[0].consentDate
										: ''
								}
								className='lg:col-span-2'
								labelClass='sm'
								type='date'
								inputClasses='max-h-[50px]'
								label={t('consentDate')}
								//@ts-ignore
								errors={errors.consentDate}
							/>
						)}
					</div>
				</form>
			)}
		</NewPatientStep>
	);
};
